import React, { useCallback, useState } from "react";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import {
	CsvParser,
	Icon,
	Loading,
	PrimaryButton,
	SecondaryButton
} from "@prestosports/commons-ui";
import { PlayerStatus } from "@prestosports/prestostats-core/Common";
import Add32 from "@carbon/icons-react/lib/add/32";
import Delete32 from "@carbon/icons-react/lib/delete/32";
import Grid from "@material-ui/core/Grid";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import DocumentImport32 from "@carbon/icons-react/lib/document--import/32";
import User32 from "@carbon/icons-react/lib/user/32";
import { usePlayersContext } from "../../../state/players/context";
import commonStyles from "../styles/Common.styles";
import { useAppContext } from "../../../state/app/context";
import teamPlayersContainerStyles from "../styles/TeamPlayersContainer.styles";
import StartImportContainer from "./StartImportContainer";

const STEP = {
	MAIN: "MAIN",
	ADD_PLAYER: "ADD_PLAYER",
	START_IMPORT_PLAYERS: "START_IMPORT_PLAYERS",
	IMPORT_PLAYERS: "IMPORT_PLAYERS"
};

const TeamPlayersContainer = ({
	team,
	TeamImportContainer,
	PlayerListContainer,
	AddPlayerContainer,
	CsvDownloadButton
}) => {
	const commonClasses = commonStyles();
	const buttonSize = useMediaQuery((theme) => theme.breakpoints.down("xs"))
		? "small"
		: "medium";
	const {
		state: playersState,
		deletePlayers,
		importPlayers,
		parseCsv,
		cancelImportPlayers
	} = usePlayersContext();
	const isImporting = playersState.isImporting();
	const { state: appContextState } = useAppContext();
	const playerManagement = appContextState.playerManagement();

	const classes = teamPlayersContainerStyles();

	const [currentPlayer, setCurrentPlayer] = useState();
	const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
	const [deleteConfirmation, setDeleteConfirmation] = useState(false);
	const [playerType, setPlayerType] = useState(PlayerStatus.ACTIVE);
	const [step, setStep] = useState(STEP.MAIN);
	const [currentPage, setCurrentPage] = useState(0);
	const [sort, setSort] = useState(null);

	const changePlayerType = () => {
		setPlayerType(
			playerType === PlayerStatus.ACTIVE
				? PlayerStatus.INACTIVE
				: PlayerStatus.ACTIVE
		);
		setCurrentPage(0);
	};

	const addPlayer = () => {
		setCurrentPlayer({});
		setStep(STEP.ADD_PLAYER);
	};

	const goBack = () => {
		setCurrentPlayer(null);
		setStep(STEP.MAIN);
	};

	const onPlayerSelected = useCallback(
		(player) => {
			setCurrentPlayer(player);
			setStep(STEP.ADD_PLAYER);
		},
		[setCurrentPlayer, setStep]
	);

	const onSelectionChanged = useCallback(
		(playerIds = []) => {
			setSelectedPlayerIds(playerIds);
		},
		[setSelectedPlayerIds]
	);

	const onImportComplete = (results) => {
		importPlayers(team.id, results);
		setStep(STEP.IMPORT_PLAYERS);
	};

	const startImport = () => {
		cancelImportPlayers();
		setStep(STEP.START_IMPORT_PLAYERS);
	};

	const onBeforeImport = () => {
		parseCsv();
	};

	const confirmDelete = async () => {
		await deletePlayers(selectedPlayerIds);
		setSelectedPlayerIds([]);
		setDeleteConfirmation(false);
	};

	const cancelDelete = () => {
		setDeleteConfirmation(false);
	};

	const isFirstStep = () => {
		return step === STEP.MAIN;
	};

	const isAddPlayerStep = () => {
		return step === STEP.ADD_PLAYER;
	};

	const isImportPlayerStep = () => {
		return step === STEP.IMPORT_PLAYERS;
	};

	const isStartImportStep = () => {
		return step === STEP.START_IMPORT_PLAYERS;
	};

	return (
		<>
			<Box className={commonClasses.container}>
				<Box className={commonClasses.headerContainer}>
					{isFirstStep() && (
						<Grid
							container
							direction="row"
							justifyContent="space-between"
							alignItems="center"
						>
							<Grid container item xs={12} sm={4}>
								<Box className={classes.titleBox}>
									<Box>
										<Typography variant="h3">Players</Typography>
										<Typography variant="body1" color="textSecondary">
											Setup team players
										</Typography>
									</Box>
									<SecondaryButton
										size="small"
										label={`View ${
											playerType === PlayerStatus.ACTIVE ? "Inactive" : "Active"
										}`}
										startIcon={
											<Icon>
												<User32 />
											</Icon>
										}
										onClick={changePlayerType}
									/>
								</Box>
							</Grid>
							{playerManagement && (
								<Grid
									container
									item
									xs={12}
									sm={8}
									direction="row"
									alignItems="center"
									spacing={2}
									className={classes.buttonContainer}
								>
									<Grid item>
										<SecondaryButton
											size={buttonSize}
											label="New Player"
											startIcon={
												<Icon>
													<Add32 />
												</Icon>
											}
											onClick={addPlayer}
										/>
									</Grid>
									<Grid item>
										<SecondaryButton
											size={buttonSize}
											label="Import"
											startIcon={
												<Icon>
													<DocumentImport32 />
												</Icon>
											}
											onClick={startImport}
										/>
									</Grid>
									<Grid item>
										<PrimaryButton
											label={
												selectedPlayerIds.length
													? `${selectedPlayerIds.length} Selected`
													: "Delete"
											}
											startIcon={
												<Icon>
													<Delete32 />
												</Icon>
											}
											size={buttonSize}
											onClick={() => setDeleteConfirmation(true)}
											disabled={selectedPlayerIds.length < 1}
										/>
									</Grid>
								</Grid>
							)}
						</Grid>
					)}
					{isAddPlayerStep() && (
						<Grid container>
							<Grid item>
								<Typography variant="h3">New Player</Typography>
								<Typography variant="body1">Add new player</Typography>
							</Grid>
						</Grid>
					)}
					{isStartImportStep() && (
						<Grid
							container
							direction="row"
							justifyContent="space-between"
							alignItems="center"
						>
							<Grid container item xs={12} sm={4}>
								<Box>
									<Typography variant="h3">Roster Import</Typography>
									<Typography variant="body1">
										Import multiple players by csv with the following header
									</Typography>
								</Box>
							</Grid>

							<Grid
								container
								item
								xs={12}
								sm={8}
								direction="row"
								alignItems="center"
								spacing={2}
								className={classes.buttonContainer}
							>
								<Grid item>
									<CsvDownloadButton />
								</Grid>
								<Grid item>
									<CsvParser
										size={buttonSize}
										label="Import"
										color="secondary"
										header
										InputProps={{
											accept: ".csv,.xlsx,.xls"
										}}
										onComplete={onImportComplete}
										onBefore={onBeforeImport}
									/>
								</Grid>
							</Grid>
						</Grid>
					)}
					{isImportPlayerStep() && (
						<Grid container>
							<Grid item>
								<Typography variant="h3">Roster Import</Typography>
								<Typography variant="body1">Review data for players</Typography>
							</Grid>
						</Grid>
					)}
				</Box>
				{isImporting ? (
					<Box className={commonClasses.loadingContainer}>
						<Loading isFetching />
					</Box>
				) : (
					<Box className={commonClasses.contentContainer}>
						{isFirstStep() && (
							<PlayerListContainer
								team={team}
								onRowAction={onPlayerSelected}
								onSelectionChanged={onSelectionChanged}
								selectedPlayerIds={selectedPlayerIds}
								currentPage={currentPage}
								onPageChanged={(_, page) => {
									setCurrentPage(page);
								}}
								onSortChanged={(sortField, sortDirection) => {
									setSort({ sortField, sortDirection });
								}}
								sortField={sort?.sortField}
								sortDirection={sort?.sortDirection}
								playerType={playerType}
							/>
						)}
						{isAddPlayerStep() && (
							<AddPlayerContainer
								team={team}
								player={currentPlayer}
								onSave={goBack}
								onCancel={goBack}
							/>
						)}
						{isStartImportStep() && <StartImportContainer onCancel={goBack} />}
						{isImportPlayerStep() && (
							<TeamImportContainer onCancel={goBack} onSave={goBack} />
						)}
					</Box>
				)}
			</Box>

			<Dialog
				open={deleteConfirmation}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
				onClose={cancelDelete}
			>
				<DialogTitle id="alert-dialog-title">Delete player?</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						Are you sure you want to delete the selected players?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={cancelDelete} color="secondary">
						No
					</Button>
					<Button onClick={confirmDelete} color="primary">
						Yes
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default TeamPlayersContainer;
